import { useEffect, type RefObject } from 'react'

/**
 * A click into an iframe (the preview browser) never reaches this document, so
 * popovers and panels missed it and stayed open. Focus moving into an iframe
 * is that click: pass it on as a pointerdown on the body, which every Radix
 * layer (and useOutside) reads as a click outside.
 */
export function closeOnIframeFocus(): void {
  window.addEventListener('blur', () => {
    setTimeout(() => {
      if (document.activeElement?.tagName !== 'IFRAME') return
      document.body.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }))
    }, 0)
  })
}

/**
 * Calls `onOutside` on a press outside `ref`. Presses in a Radix portal (a
 * menu or popover opened from inside) count as inside.
 */
export function useOutside(ref: RefObject<HTMLElement | null>, onOutside: () => void): void {
  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Element | null
      if (!t || ref.current?.contains(t)) return
      if (t.closest?.('[data-radix-popper-content-wrapper], [role="dialog"], [role="menu"], [role="listbox"]')) return
      onOutside()
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [ref, onOutside])
}
